import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { buzz, nudge, unlock } from '../../lib/chime';
import { osNotify } from '../../lib/osNotify';
import { amSilenced } from '../../lib/chatStatus';
import { claimRing } from '../../lib/ringOnce';
import { addDays, longDay } from '../../lib/quickAdd';
import { dueChip, timeLabel } from '../../lib/when';
import { Icon } from './icons';
import './ReminderAlerts.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const POLL_MS = 30 * 1000;
const CHANNEL = 'jarvis-reminders';
// The bell carries the same reminders (an item each, until answered): told
// when one is answered here, so the two never disagree for a minute.
const told = () => { try { window.dispatchEvent(new CustomEvent('jarvis:reminders-changed')); } catch { /* old browser */ } };

/*
 * How many cards stand in the corner before the rest fold into "2 more due":
 * two on a desk, where three full cards took a third of the screen, and one
 * on a phone, where they took half of it.
 */
const PHONE = '(max-width: 640px)';
const isPhone = () => { try { return window.matchMedia(PHONE).matches; } catch { return false; } };
function usePhone() {
  const [phone, setPhone] = useState(isPhone);
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(PHONE); } catch { return undefined; }
    const on = () => setPhone(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else if (mq.addListener) mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else if (mq.removeListener) mq.removeListener(on); };
  }, []);
  return phone;
}

/**
 * "Due 15:00", "Due today", "Due tomorrow 09:00", "Due Fri" - the day in
 * lower case mid-sentence, the time 24-hour (lib/when). The card, the desktop
 * notification and the bell all say it this way.
 */
export const dueSaid = (date, time, now = new Date()) => {
  const d = date ? String(date).slice(0, 10) : null;
  const t = time ? timeLabel(time) : null;
  if (!d) return t ? `Due ${t}` : 'Due now';
  const chip = dueChip(d, t, now);
  if (/^Today/.test(chip)) return t ? `Due ${t}` : 'Due today';
  return `Due ${chip.replace(/^(Tomorrow|Yesterday)/, (w) => w.toLowerCase())}`;
};

/** Minutes from now to tomorrow at nine, inside what the server takes (5..2880). */
const untilTomorrowNine = () => {
  const now = new Date();
  const t = addDays(now, 1);
  t.setHours(9, 0, 0, 0);
  return Math.max(5, Math.min(2880, Math.round((t - now) / 60000)));
};
const SNOOZES = [
  { label: '10 min', minutes: () => 10 },
  { label: '1 hour', minutes: () => 60 },
  { label: 'Tomorrow 09:00', minutes: untilTomorrowNine }
];

/** One reminder falling due once: a snoozed one comes back with a new fire_at, so a new key. */
const keyOf = (r) => `${r.reminder_id}@${r.fire_at || ''}`;

/**
 * A nudge when a to-do is due, wherever in Jarvis somebody is.
 *
 * Gordon, batch 3: reminders - "a nudge when something is due: bell, sound,
 * desktop notification". This is the sound, the desktop notification and a
 * card of its own; the bell's half is in NotificationsBell.
 *
 * MOUNTED IN THE SHELL, like IncomingCall and for the same reason: a
 * reminder that only rings on the to-do page rings for the people already
 * looking at their to-dos. It asks GET /todos/reminders/due every thirty
 * seconds, and again whenever the window comes back into focus - which is
 * when somebody back from a meeting most wants to know. The answer marks
 * nothing: a reminder is due until it is answered here (Open, Done or
 * Dismiss acknowledge it; Snooze puts it off) - so a card answered in one
 * tab leaves the others on their next look, and at once where the browser
 * can say so (a BroadcastChannel).
 *
 * ONE TAB RINGS. Every open tab sees the same reminder fall due; only the one
 * that wins lib/ringOnce's claim plays the chime and raises the desktop
 * notification. Every tab shows the card.
 *
 * QUIET WHEN IT SHOULD BE. "Sound off" (lib/chime) silences the chime, Do not
 * disturb (lib/chatStatus) the chime and the desktop notification; the card
 * still comes up, because a reminder nobody sees is not a reminder. A server
 * without to-do reminders answers 503 (not migrated) or 404, and somebody
 * signed out 401: then this stops asking for as long as it is mounted, and
 * says nothing - it is not this corner's job to report the server.
 *
 * WHOSE. `token` is a contractor's own; staff send nothing and the app's
 * interceptor attaches theirs. `asParam` is "c:<id>" when an administrator
 * is looking through a contractor's portal - the same as the to-do page, so
 * "Open" lands on the list the card came from. `todoPath` is where that is.
 */
export default function ReminderAlerts({ token, asParam = null, todoPath = '/todos' }) {
  const [cards, setCards] = useState([]);
  const [menu, setMenu] = useState(null);       // reminder id whose Snooze is open
  const [note, setNote] = useState(null);       // { key, text } - "Snoozed until …"
  const [busyId, setBusyId] = useState(null);
  const [all, setAll] = useState(false);        // "2 more due" pressed: every card, not the newest
  const phone = usePhone();
  const navigate = useNavigate();
  const stopped = useRef(false);
  const seen = useRef(new Set());               // keys this tab has already had its chance to ring
  const gone = useRef(new Set());               // answered here: kept off until the server agrees
  const chan = useRef(null);
  const openRef = useRef(null);

  const cfg = useCallback(() => ({
    ...(asParam ? { params: { as: asParam } } : {}),
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {})
  }), [token, asParam]);

  const say = (text) => {
    const key = `${Date.now()}-${Math.random()}`;
    setNote({ key, text });
    setTimeout(() => setNote((n) => (n && n.key === key ? null : n)), 4000);
  };

  const ring = useCallback(async (r) => {
    let won = false;
    try { won = await claimRing(`todo-${keyOf(r)}`); } catch { won = true; }
    if (!won || amSilenced()) return;
    nudge();
    buzz([120, 80, 120]);
    osNotify({
      title: r.title || 'A to-do is due',
      body: dueSaid(r.due_date, r.due_time, new Date()),
      tag: `todo-reminder-${r.reminder_id}`,
      whenUnfocused: true,
      replace: false,
      kind: 'todo_reminders',
      onClick: () => { if (openRef.current) openRef.current(r); }
    });
  }, []);

  const poll = useCallback(async () => {
    if (stopped.current) return;
    let rows;
    try {
      const res = await axios.get(`${API_BASE}/todos/reminders/due`, cfg());
      const d = res.data;
      rows = Array.isArray(d) ? d : Array.isArray(d?.reminders) ? d.reminders : [];
    } catch (err) {
      const s = err?.response?.status;
      if (s === 503 || s === 401 || s === 403 || s === 404) stopped.current = true;
      return;
    }
    rows = rows.filter((r) => r && r.reminder_id !== null && r.reminder_id !== undefined
      && r.todo_id !== null && r.todo_id !== undefined);
    const live = new Set(rows.map((r) => String(r.reminder_id)));
    for (const id of [...gone.current]) if (!live.has(id)) gone.current.delete(id);
    const show = rows.filter((r) => !gone.current.has(String(r.reminder_id)));
    setCards(show);
    const fresh = show.filter((r) => !seen.current.has(keyOf(r)));
    fresh.forEach((r) => seen.current.add(keyOf(r)));
    // One chime for a handful falling due together, not one each.
    if (fresh.length) ring(fresh[fresh.length - 1]);
  }, [cfg, ring]);

  useEffect(() => {
    stopped.current = false;
    poll();
    const id = setInterval(() => {
      if (stopped.current) { clearInterval(id); return; }
      poll();
    }, POLL_MS);
    const look = () => { if (!document.hidden) poll(); };
    window.addEventListener('focus', look);
    document.addEventListener('visibilitychange', look);
    window.addEventListener('jarvis:todos-changed', look);
    window.addEventListener('jarvis:reminders-changed', look);
    return () => {
      window.removeEventListener('jarvis:reminders-changed', look);
      clearInterval(id);
      window.removeEventListener('focus', look);
      document.removeEventListener('visibilitychange', look);
      window.removeEventListener('jarvis:todos-changed', look);
    };
  }, [poll]);

  // Another tab answered one: off here too, without waiting for the next look.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    let c;
    try { c = new BroadcastChannel(CHANNEL); } catch { return undefined; }
    chan.current = c;
    c.onmessage = (e) => {
      const id = e?.data?.gone;
      if (id === undefined || id === null) return;
      gone.current.add(String(id));
      setCards((prev) => prev.filter((r) => String(r.reminder_id) !== String(id)));
    };
    return () => { chan.current = null; try { c.close(); } catch { /* gone */ } };
  }, []);

  // The chime needs a gesture first (see lib/chime); IncomingCall asks the
  // same, and asking twice costs nothing.
  useEffect(() => {
    const open = () => unlock();
    for (const type of ['pointerdown', 'keydown']) window.addEventListener(type, open, { once: true, passive: true });
    return () => { for (const type of ['pointerdown', 'keydown']) window.removeEventListener(type, open); };
  }, []);

  useEffect(() => {
    if (menu === null) return undefined;
    const away = (e) => { if (!e.target.closest || !e.target.closest('.ra-snooze')) setMenu(null); };
    const esc = (e) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [menu]);

  const drop = (r) => {
    const id = String(r.reminder_id);
    gone.current.add(id);
    setCards((prev) => prev.filter((c) => String(c.reminder_id) !== id));
    setMenu(null);
    try { chan.current?.postMessage({ gone: id }); } catch { /* one tab, then */ }
  };
  const back = (r) => { gone.current.delete(String(r.reminder_id)); poll(); };
  const ack = (r) => axios.post(`${API_BASE}/todos/reminders/${encodeURIComponent(r.reminder_id)}/ack`, {}, cfg())
    .catch(() => {}).then(told);

  const dismiss = (r) => { drop(r); ack(r); };

  const open = (r) => {
    drop(r);
    ack(r);
    navigate(`${todoPath}?item=${encodeURIComponent(r.todo_id)}`);
  };
  openRef.current = open;

  const snooze = (r, s) => {
    const minutes = s.minutes();
    drop(r);
    axios.post(`${API_BASE}/todos/reminders/${encodeURIComponent(r.reminder_id)}/snooze`, { minutes }, cfg())
      .then(() => { told(); say(s.label === 'Tomorrow 09:00' ? 'Snoozed until tomorrow at 09:00' : `Snoozed for ${s.label}`); })
      .catch(() => { say('That snooze did not go through'); back(r); });
  };

  // Done is the to-do's own tick, so a repeating one moves on to its next
  // date (and says when) exactly as ticking it on the page would.
  const done = async (r) => {
    const id = String(r.reminder_id);
    setBusyId(id);
    try {
      const res = await axios.patch(`${API_BASE}/todos/${encodeURIComponent(r.todo_id)}`, { done: true }, cfg());
      drop(r);
      ack(r);
      const moved = res?.data?.repeated;
      say(moved && moved.to ? `Done · next ${longDay(String(moved.to).slice(0, 10))}` : `Done: ${r.title || 'to-do'}`);
      window.dispatchEvent(new CustomEvent('jarvis:todos-changed'));
    } catch {
      say('That did not go through');
    } finally {
      setBusyId(null);
    }
  };

  if (!cards.length && !note) return null;
  const now = new Date();
  const max = phone ? 1 : 2;
  const folded = cards.length > max && !all;
  const shown = (folded ? cards.slice(-max) : cards.slice()).reverse();
  const more = cards.length - max;

  /*
   * A card: the bell, the title (two lines at most) and when, then Open,
   * Snooze and Done, with Dismiss a quiet × in the corner. The title is a
   * button too - on a phone the card is a single bar, Snooze and Done beside
   * the words and Open the words themselves.
   */
  return (
    <div className={`ra-stack${all && cards.length > max ? ' is-all' : ''}`} role="region" aria-label="Reminders">
      {note && <div className="ra-note" role="status">{note.text}</div>}
      {cards.length > max && (
        <button type="button" className="ra-more" aria-expanded={!folded} onClick={() => setAll((v) => !v)}>
          {folded ? `${more} more due` : 'Show fewer'}
          <Icon name="chevronDown" size={14} className={folded ? '' : 'ra-up'} />
        </button>
      )}
      <div className="ra-cards">
        {shown.map((r) => {
          const id = String(r.reminder_id);
          return (
            <div key={id} className="ra-card" role="alert" data-reminder={id} data-todo={r.todo_id}>
              <span className="ra-icon"><Icon name="bell" size={16} /></span>
              <button type="button" className="ra-text" onClick={() => open(r)}>
                <strong className="ra-title">{r.title || 'A to-do'}</strong>
                <span className="ra-due">{dueSaid(r.due_date, r.due_time, now)}</span>
              </button>
              <div className="ra-buttons">
                <button type="button" className="ra-btn is-main ra-open" onClick={() => open(r)}>Open</button>
                <div className="ra-snooze">
                  <button type="button" className="ra-btn ra-snooze-btn" aria-haspopup="menu" aria-expanded={menu === id}
                          onClick={() => setMenu(menu === id ? null : id)}>
                    <Icon name="clock" size={16} className="ra-phone-only" />
                    <span className="ra-word">Snooze</span>
                    <Icon name="chevronDown" size={14} className="ra-desk-only" />
                  </button>
                  {menu === id && (
                    <div className="ra-menu" role="menu" aria-label="Snooze for">
                      {SNOOZES.map((s) => (
                        <button key={s.label} type="button" role="menuitem" className="ra-menu-item"
                                onClick={() => snooze(r, s)}>{s.label}</button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" className="ra-btn" disabled={busyId === id} onClick={() => done(r)}>Done</button>
              </div>
              <button type="button" className="ra-x" aria-label="Dismiss" title="Dismiss" onClick={() => dismiss(r)}>
                <Icon name="close" size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
